// Bank Account Creation verification (legacy /AccountsApproval/VerifyBankAccounts).
// Pending rows are verified/approved via the shared status list; rows returned for update
// (Status '0') get legacy's inline resubmit form instead (Location + Minimum Balance editable).
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Landmark, Clock, MapPin, RotateCcw, Send } from 'lucide-react';

import InboxHeader from '../../components/Inbox/InboxHeader';
import ActionButtons from '../../components/Inbox/ActionButtons';
import RemarksHistory from '../../components/Inbox/RemarksHistory';
import InboxSplitLayout from '../../components/Inbox/InboxSplitLayout';
import { SimpleCommentInput } from '../../components/Inbox/VerificationInput';
import { formatIndianCurrency } from '../../utilities/amountToTextHelper';

import {
    fetchVerifyBankAccountsQueue,
    fetchVerifyBankAccountDetail,
    approveVerifyBankAccount,
    updateReturnedBankAccount,
    clearVerifyBankAccountDetail,
    resetVerifyBankAccounts,
    selectVBAQueue, selectVBAQueueLoading, selectVBAQueueError,
    selectVBADetail, selectVBADetailLoading, selectVBADetailError,
    selectVBAVerifyLoading, selectVBAUpdateLoading,
} from '../../slices/accountsSlice/verifyBankAccountsSlice';

import {
    fetchStatusList,
    selectEnabledActions,
    selectHasActions,
    selectStatusListLoading,
    selectStatusListError,
    resetApprovalData,
    setShowReturnButton,
} from '../../slices/CommonSlice/getStatusSlice';

import {
    fetchRemarks,
    selectRemarks,
    selectRemarksLoading,
    setSelectedMOID,
} from '../../slices/supplierPOSlice/purcahseHelperSlice';

const ACTION_VERB = {
    Verify: 'Verified Successfully',
    Approve: 'Approved Successfully',
    Return: 'Returned for Update',
    Reject: 'Rejected Successfully',
};

const OVER_DRAFT = 'Over Draft';

const Field = ({ label, value, className = '' }) => (
    <div className={className}>
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">{label}</p>
        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 break-words">{value || value === 0 ? value : '—'}</p>
    </div>
);

const StatusBadge = ({ status }) => (status === '0' ? (
    <span className="px-2 py-0.5 text-xs rounded-full border bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 whitespace-nowrap">Return</span>
) : (
    <span className="px-2 py-0.5 text-xs rounded-full border bg-indigo-100 text-indigo-800 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 whitespace-nowrap">Verification Pending</span>
));

const inputCls = 'w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500';

const formatAmount = (v) => (v != null && v !== '' ? `₹ ${formatIndianCurrency(v)}` : null);

const VerifyBankAccounts = ({ notificationData, onNavigate }) => {
    const dispatch = useDispatch();
    const { userData, userDetails } = useSelector((s) => s.auth);
    const roleId = userData?.roleId || userData?.RID;

    const queue = useSelector(selectVBAQueue);
    const queueLoading = useSelector(selectVBAQueueLoading);
    const queueError = useSelector(selectVBAQueueError);
    const detail = useSelector(selectVBADetail);
    const detailLoading = useSelector(selectVBADetailLoading);
    const detailError = useSelector(selectVBADetailError);
    const verifyLoading = useSelector(selectVBAVerifyLoading);
    const updateLoading = useSelector(selectVBAUpdateLoading);

    const statusLoading = useSelector(selectStatusListLoading);
    const statusError = useSelector(selectStatusListError);
    const enabledActions = useSelector(selectEnabledActions);
    const hasActions = useSelector(selectHasActions);

    const remarks = useSelector(selectRemarks);
    const remarksLoading = useSelector(selectRemarksLoading);

    const { InboxTitle, ModuleDisplayName } = notificationData || {};

    const [selectedItem, setSelectedItem] = useState(null);
    const [comment, setComment] = useState('');
    const [showRemarksHistory, setShowRemarksHistory] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false);
    const [isLeftPanelHovered, setIsLeftPanelHovered] = useState(false);
    // Returned-row resubmit form
    const [updLocation, setUpdLocation] = useState('');
    const [updMinBalance, setUpdMinBalance] = useState('');

    const getCurrentUser = () => userData?.userName || userDetails?.userName || 'system';

    // Only trust the detail that belongs to the row currently selected.
    const bank = selectedItem && detail?.Bankid === selectedItem.Bankid ? detail : null;
    const isReturned = selectedItem?.Status === '0';

    useEffect(() => {
        if (roleId) dispatch(fetchVerifyBankAccountsQueue(roleId));
    }, [roleId, dispatch]);

    useEffect(() => {
        // Legacy's status dropdown keeps "Return" (it is only dropped for Closed accounts, below).
        dispatch(setShowReturnButton('Yes'));
        return () => {
            dispatch(resetVerifyBankAccounts());
            dispatch(resetApprovalData());
        };
    }, [dispatch]);

    useEffect(() => {
        if (!selectedItem) return;
        dispatch(clearVerifyBankAccountDetail());
        dispatch(resetApprovalData());
        dispatch(setShowReturnButton('Yes'));
        dispatch(fetchVerifyBankAccountDetail(selectedItem.Bankid));
        setComment('');
        setShowRemarksHistory(false);
        setIsLeftPanelCollapsed(true);
        // The queue row already carries MOID, so the action list can load alongside the detail.
        if (selectedItem.Status !== '0' && selectedItem.MOID && roleId) {
            dispatch(fetchStatusList({ MOID: selectedItem.MOID, ROID: roleId, ChkAmt: 0 }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedItem]);

    // Remarks are keyed on the bank name (spApproveBankAccount writes TransactionNo = BankName).
    useEffect(() => {
        if (!bank?.MOID) return;
        dispatch(setSelectedMOID(bank.MOID));
        dispatch(fetchRemarks({ trno: bank.BankName, moid: bank.MOID }));
        setUpdLocation(bank.Banklocation || '');
        setUpdMinBalance(bank.MinimumBalance != null ? String(bank.MinimumBalance) : '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bank?.MOID, bank?.Bankid]);

    const handleBackToInbox = () => {
        if (onNavigate) onNavigate('dashboard', { name: 'Dashboard', type: 'dashboard' });
    };

    const handleRefresh = () => {
        if (roleId) dispatch(fetchVerifyBankAccountsQueue(roleId));
    };

    const finishAndRefresh = () => {
        setTimeout(() => {
            dispatch(fetchVerifyBankAccountsQueue(roleId));
            setSelectedItem(null);
            setComment('');
            setShowRemarksHistory(false);
            setIsLeftPanelCollapsed(false);
            dispatch(clearVerifyBankAccountDetail());
            dispatch(resetApprovalData());
        }, 800);
    };

    const handleActionClick = async (action) => {
        if (!bank) { toast.error('No bank account selected'); return; }
        if (!comment.trim()) { toast.error('Enter Note'); return; }

        // Legacy posts the dropdown option's Type text ("Approve" | "Verify" | "Return" | "Reject").
        const actionValue = action.type;
        const payload = {
            Bankid: bank.Bankid,
            Accountno: bank.Accountno,
            Action: actionValue,
            ApprovalNote: comment.trim(),
            BankStatus: bank.BankStatus,
            Createdby: getCurrentUser(),
            RoleId: roleId,
        };

        try {
            const result = await dispatch(approveVerifyBankAccount(payload)).unwrap();
            const status = typeof result === 'string' ? result : result?.Data;
            // spApproveBankAccount's success literal is the single-t "Submited".
            if (status === 'Submited' || status === 'Submitted') {
                toast.success(`Bank Account ${ACTION_VERB[actionValue] || `${actionValue}d`}`);
                finishAndRefresh();
                return;
            }
            toast.error(status || `${actionValue} was not applied — the server returned no confirmation.`);
        } catch (error) {
            toast.error(typeof error === 'string' ? error : 'Failed to submit bank account verification');
        }
    };

    // Legacy key filter: Over Draft accounts may carry a negative minimum balance; every other
    // type is a plain positive amount with at most 2 decimals.
    const handleMinBalanceChange = (e) => {
        const value = e.target.value;
        const pattern = bank?.AccountType === OVER_DRAFT ? /^-?\d*\.?\d{0,2}$/ : /^\d*\.?\d{0,2}$/;
        if (value === '' || pattern.test(value)) setUpdMinBalance(value);
    };

    const handleResubmit = async () => {
        if (!bank) return;
        const errors = [];
        if (!updLocation.trim()) errors.push('Enter Location');
        const minBal = parseFloat(updMinBalance);
        if (updMinBalance === '' || updMinBalance === '.' || updMinBalance === '-' || minBal === 0 || Number.isNaN(minBal)) {
            errors.push('Enter Minimum Balance');
        }
        if (!Number.isNaN(minBal) && parseFloat(bank.OpeningBalance) <= minBal) {
            errors.push('Minimum Balance should not be greater than Opening Balance');
        }
        if (errors.length) { errors.forEach((m) => toast.error(m)); return; }

        const payload = {
            Bankid: bank.Bankid,
            Banklocation: updLocation.trim(),
            Status: '1',
            MinimumBalance: minBal,
            Createdby: getCurrentUser(),
            RoleId: roleId,
            Role: userData?.roleCode || '',
        };

        try {
            const result = await dispatch(updateReturnedBankAccount(payload)).unwrap();
            const status = typeof result === 'string' ? result : result?.Data;
            if (status === 'Submitted' || status === 'Submited') {
                toast.success('Bank Account Updated Successfully');
                finishAndRefresh();
                return;
            }
            toast.error(status || 'Update was not applied — the server returned no confirmation.');
        } catch (error) {
            toast.error(typeof error === 'string' ? error : 'Failed to update bank account');
        }
    };

    const filteredQueue = queue.filter((item) => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return item.BankName?.toLowerCase().includes(q)
            || item.Accountno?.toLowerCase().includes(q)
            || item.BankType?.toLowerCase().includes(q)
            || item.Banklocation?.toLowerCase().includes(q)
            || item.NatureGroupName?.toLowerCase().includes(q);
    });

    const renderItemCard = (item) => (
        <div className="p-4">
            <div className="flex items-center space-x-3 mb-2">
                <div className="w-12 h-12 rounded-full border-2 border-indigo-200 dark:border-indigo-600 bg-gradient-to-br from-indigo-100 to-indigo-100 dark:from-indigo-800/50 dark:to-indigo-800/50 flex items-center justify-center">
                    <Landmark className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm truncate">{item.BankName}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{item.Accountno}</p>
                </div>
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                <div className="flex items-center justify-between gap-2">
                    <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-[10px]">{item.BankType || item.NatureGroupName}</span>
                    <StatusBadge status={item.Status} />
                </div>
                {item.Banklocation && (
                    <div className="flex items-start space-x-1"><MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" /><span className="line-clamp-2">{item.Banklocation}</span></div>
                )}
            </div>
        </div>
    );

    const renderListItem = (item) => (
        <div className="flex items-center gap-x-6 gap-y-1 flex-wrap text-sm">
            <span className="font-semibold text-gray-900 dark:text-white min-w-[180px] truncate">{item.BankName}</span>
            <span className="font-mono bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded text-xs">{item.Accountno}</span>
            <span className="text-gray-500 dark:text-gray-400 min-w-[110px]">{item.BankType}</span>
            <span className="text-gray-500 dark:text-gray-400 min-w-[110px]">{item.NatureGroupName}</span>
            <span className="text-gray-500 dark:text-gray-400 truncate max-w-[240px]">{item.Banklocation}</span>
            <span className="ml-auto"><StatusBadge status={item.Status} /></span>
        </div>
    );

    const renderCollapsedItem = () => (
        <div className="w-full h-full rounded-lg border-2 border-indigo-200 dark:border-indigo-600 bg-gradient-to-br from-indigo-100 to-indigo-100 dark:from-indigo-800/50 dark:to-indigo-800/50 flex items-center justify-center">
            <Landmark className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
        </div>
    );

    const renderBankDetails = () => (
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border-2 border-gray-200 dark:border-gray-700 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Field label="Bank Name" value={bank.BankName} />
                <Field label="Account Number" value={bank.Accountno} />
                {!isReturned && <Field label="Location" value={bank.Banklocation} />}
                <Field label="Nature of Group" value={bank.NatureGroupName} />
                <Field label="Account Type" value={bank.AccountType} />
                <Field label="Account Holder Name" value={bank.AccountHolderName} />
                <Field label="Opening Balance" value={formatAmount(bank.OpeningBalance)} />
                <Field label="Opening Date" value={bank.AccOpeningDate} />
                <Field label="Balance As On Date" value={bank.BalanceAsOn} />
                {!isReturned && <Field label="Minimum Balance" value={formatAmount(bank.MinimumBalance)} />}
                {!isReturned && <Field label="Bank Status" value={bank.BankStatus} />}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <Field label="Group Name" value={bank.GroupName} />
                {bank.SubGroupId && bank.SubGroupId !== '0' && <Field label="Sub Group Name" value={bank.SubGroupName} />}
                <Field label="Ledger Value Type" value={bank.LedgerValueType} />
            </div>
        </div>
    );

    const renderResubmitForm = () => (
        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-2xl p-5 border-2 border-amber-200 dark:border-amber-700 space-y-4">
            <div className="flex items-center gap-2">
                <RotateCcw className="w-4 h-4 text-amber-700 dark:text-amber-400" />
                <h3 className="text-sm font-bold text-amber-800 dark:text-amber-300">Returned for Update — correct and resubmit</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <label className="block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Location <span className="text-red-500">*</span></label>
                    <input type="text" className={inputCls} value={updLocation} onChange={(e) => setUpdLocation(e.target.value)} />
                </div>
                <div>
                    <label className="block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Minimum Balance <span className="text-red-500">*</span></label>
                    <input type="text" inputMode="decimal" className={inputCls} value={updMinBalance} onChange={handleMinBalanceChange} />
                </div>
            </div>
            <div className="flex justify-end">
                <button
                    type="button"
                    onClick={handleResubmit}
                    disabled={updateLoading}
                    className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                >
                    {updateLoading ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> : <Send className="w-4 h-4" />}
                    Resubmit
                </button>
            </div>
        </div>
    );

    const renderActions = () => {
        if (statusLoading) {
            return (
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-center space-x-3">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                        <span className="text-gray-600 dark:text-gray-400">Loading actions…</span>
                    </div>
                </div>
            );
        }
        if (statusError) {
            return (
                <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 border border-red-200 dark:border-red-700">
                    <p className="text-red-600 dark:text-red-400 text-center">⚠️ Error loading actions: {statusError}</p>
                </div>
            );
        }
        if (!hasActions || !enabledActions || enabledActions.length === 0) {
            return (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-xl p-6 border border-yellow-200 dark:border-yellow-700">
                    <p className="text-yellow-700 dark:text-yellow-400 text-center">ℹ️ No actions available for this bank account</p>
                </div>
            );
        }
        return (
            <ActionButtons
                actions={enabledActions}
                onActionClick={handleActionClick}
                loading={verifyLoading}
                isVerified={true}
                comment={comment}
                showValidation={true}
                // Legacy drops Return and Reject from the dropdown when the account is being closed.
                excludeActions={bank.BankStatus === 'Closed' ? ['return', 'reject'] : []}
            />
        );
    };

    const renderDetailContent = () => {
        if (!selectedItem) return null;

        if (detailLoading || (!bank && !detailError)) {
            return (
                <div className="flex items-center justify-center gap-3 py-12">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">Loading bank account details…</span>
                </div>
            );
        }
        if (detailError || !bank) {
            return (
                <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 border border-red-200 dark:border-red-700">
                    <p className="text-red-600 dark:text-red-400 text-center">⚠️ {detailError || 'Bank account details could not be loaded — it may no longer be pending at your level.'}</p>
                </div>
            );
        }

        return (
            <div className="space-y-6">
                <div className="bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-2xl p-6 border-2 border-indigo-200 dark:border-indigo-700">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">{bank.BankName}</h2>
                    <p className="text-indigo-600 dark:text-indigo-400 font-semibold mb-3">{bank.Accountno}</p>
                    <div className="flex flex-wrap gap-2">
                        {selectedItem.BankType && (
                            <span className="px-3 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-full text-xs font-medium">{selectedItem.BankType}</span>
                        )}
                        {bank.AccountType && (
                            <span className="px-3 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-full text-xs font-medium">{bank.AccountType}</span>
                        )}
                        {bank.BankStatus && (
                            <span className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-xs font-medium">Bank Status: {bank.BankStatus}</span>
                        )}
                        <StatusBadge status={selectedItem.Status} />
                    </div>
                </div>

                {renderBankDetails()}

                <RemarksHistory
                    isOpen={showRemarksHistory}
                    onToggle={() => setShowRemarksHistory(!showRemarksHistory)}
                    remarks={remarks}
                    loading={remarksLoading}
                    title="Approval History"
                />

                {isReturned ? renderResubmitForm() : (
                    <>
                        <SimpleCommentInput
                            comment={comment}
                            onCommentChange={(e) => setComment(e.target.value)}
                            label="Notes"
                            placeholder="Enter verification notes…"
                            required
                            rows={3}
                            showCharCount
                        />
                        {renderActions()}
                    </>
                )}
            </div>
        );
    };

    return (
        <div className="space-y-6">
            <InboxHeader
                title={`${InboxTitle || 'Bank Accounts Verification'} (${queue.length})`}
                subtitle={ModuleDisplayName}
                itemCount={queue.length}
                onBackClick={handleBackToInbox}
                HeaderIcon={Landmark}
                badgeText="Bank Accounts Verification"
                badgeCount={queue.length}
                searchConfig={{
                    enabled: true,
                    placeholder: 'Search by bank, account number, type, location…',
                    value: searchQuery,
                    onChange: (e) => setSearchQuery(e.target.value),
                }}
                enableViewToggle
            />

            <InboxSplitLayout
                isLeftPanelCollapsed={isLeftPanelCollapsed}
                onLeftPanelCollapseToggle={setIsLeftPanelCollapsed}
                isLeftPanelHovered={isLeftPanelHovered}
                onLeftPanelHoverChange={setIsLeftPanelHovered}
                left={{
                    items: filteredQueue,
                    selectedItem,
                    onItemSelect: setSelectedItem,
                    renderItem: renderItemCard,
                    renderListItem,
                    renderCollapsedItem,
                    loading: queueLoading,
                    error: queueError,
                    onRefresh: handleRefresh,
                    config: {
                        title: 'Pending',
                        icon: Clock,
                        emptyMessage: 'No bank accounts pending verification!',
                        itemKey: 'Bankid',
                        enableCollapse: true,
                        enableRefresh: true,
                        enableHover: true,
                        maxHeight: '100%',
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                    },
                    renderPopupContent: () => renderDetailContent(),
                    popupConfig: {
                        title: 'Bank Accounts Verification',
                        icon: Landmark,
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                        maxWidth: 'max-w-[80vw]',
                    },
                }}
                right={{
                    selectedItem,
                    loading: false,
                    renderContent: renderDetailContent,
                    config: {
                        title: 'Bank Account Details',
                        icon: Landmark,
                        selectedTitle: 'Bank Accounts Verification',
                        emptyTitle: 'No Bank Account Selected',
                        emptyMessage: 'Select a bank account from the list to view details and take action.',
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                        maxHeight: 'calc(100vh-200px)',
                        sticky: true,
                        stickyTop: '1.5rem',
                    },
                }}
            />
        </div>
    );
};

export default VerifyBankAccounts;
